import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { LineChart, ArrowLeft, Check, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import '../styles/reachCurvePlanner.css';

function ReachCurvePlanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [currentAudience, setCurrentAudience] = useState(null);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [processingData, setProcessingData] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    audienceName: '',
    budget: 100000,
    channelMix: {}
  });
  const [totalAllocation, setTotalAllocation] = useState(0);
  const [calculationId, setCalculationId] = useState(null);
  const [showAudienceSelector, setShowAudienceSelector] = useState(false);
  const [availableAudiences, setAvailableAudiences] = useState([]);
  const [audienceLoading, setAudienceLoading] = useState(false);

  // Get audience from session storage
  useEffect(() => {
    const storedAudience = sessionStorage.getItem('currentAudience');
    if (storedAudience) {
      const audience = JSON.parse(storedAudience);
      setCurrentAudience(audience);
      setFormData(prev => ({
        ...prev,
        audienceName: audience.name
      }));
    } else {
      // Instead of redirecting, fetch available audiences
      fetchAvailableAudiences();
    }
  }, []);
  
  // Function to fetch available audiences from Airtable using GET
  const fetchAvailableAudiences = async () => {
    setAudienceLoading(true);
    try {
      const response = await axios.get(
        'https://n8n.iniai.cloud/webhook/get-prob-audiences',
        {
          params: {
            userId: user?.id || '',
            userName: user?.name || ''
          },
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY
          }
        }
      );
      
      if (response.data && Array.isArray(response.data)) {
        setAvailableAudiences(response.data);
      }
      setShowAudienceSelector(true);
    } catch (error) {
      console.error('Error fetching audiences:', error);
    } finally {
      setAudienceLoading(false);
    }
  };
  
  // Function to select an audience
  const handleAudienceSelect = (audience) => {
    // Format audience data as needed
    const selectedAudience = {
      name: audience['Audience Name'],
      size: audience['Audience Size'].toLocaleString(),
      market: audience['Audience Market']
    };
    
    // Save to session storage and update state
    sessionStorage.setItem('currentAudience', JSON.stringify(selectedAudience));
    setCurrentAudience(selectedAudience);
    setFormData(prev => ({
      ...prev,
      audienceName: selectedAudience.name
    }));
    setShowAudienceSelector(false);
    
    // Reset any previous channels
    setAvailableChannels([]);
    setFormData(prev => ({
      ...prev,
      channelMix: {}
    }));
    setTotalAllocation(0);
    
    // Fetch channels for the new audience
    fetchChannels(selectedAudience);
  };
  
  // Function to change audience
  const handleChangeAudience = () => {
    // Show audience selector
    fetchAvailableAudiences();
  };

  // Fetch available channels from Airtable
  useEffect(() => {
    if (currentAudience) {
      fetchChannels(currentAudience);
    }
  }, [currentAudience]);
  
  // Updated function to fetch available channels using GET
  const fetchChannels = async (audience) => {
    if (!audience) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        'https://n8n.iniai.cloud/webhook/get-channels',
        {
          params: {
            market: audience.market
          },
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY
          }
        }
      );

      if (Array.isArray(response.data) && response.data.length > 0) {
        const channels = response.data.map(channel => ({
          id: channel.id,
          name: channel['Channel Name'],
          platform: channel.Platform,
          baseCPM: channel['Base CPM']
        }));
        
        setAvailableChannels(channels);
        
        // Initialize channel mix with 0% for each channel
        const initialMix = {};
        channels.forEach(channel => {
          initialMix[channel.name] = 0;
        });
        
        setFormData(prev => ({
          ...prev,
          channelMix: initialMix
        }));
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
      setError('Failed to load available channels. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate total allocation whenever channelMix changes
  useEffect(() => {
    let total = 0;
    Object.values(formData.channelMix).forEach(value => {
      total += Number(value) || 0;
    });
    setTotalAllocation(total);
  }, [formData.channelMix]);

  const handleChannelAllocationChange = (channelName, value) => {
    // Ensure value is a number between 0 and 100
    let numberValue = Number(value);
    if (isNaN(numberValue)) numberValue = 0;
    if (numberValue < 0) numberValue = 0;
    if (numberValue > 100) numberValue = 100;

    setFormData(prev => ({
      ...prev,
      channelMix: {
        ...prev.channelMix,
        [channelName]: numberValue
      }
    }));
  };

  const handleBudgetChange = (e) => {
    let value = Number(e.target.value);
    if (isNaN(value) || value < 0) value = 0;
    
    setFormData(prev => ({
      ...prev,
      budget: value
    }));
  };

  // Updated handleSubmit function with fixes
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate total allocation
    if (totalAllocation !== 100) {
      setError('Total channel allocation must equal 100%');
      return;
    }
    
    // Filter out channels with 0% allocation
    const filteredChannelMix = {};
    Object.entries(formData.channelMix).forEach(([channel, allocation]) => {
      if (allocation > 0) {
        filteredChannelMix[channel] = allocation;
      }
    });
    
    // First, trigger data preparation workflow
    setProcessingData(true);
    setError(null);
    
    try {
      // Step 1: Prepare data by consolidating audience, channel, and affinity data
      console.log("Preparing data for audience:", formData.audienceName);
      const prepResponse = await axios.post(
        'https://n8n.iniai.cloud/webhook/integrated-data',
        {
          audience: formData.audienceName // Direct string as expected by the webhook
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY
          }
        }
      );
      
      console.log("Data preparation response:", prepResponse.data);
      
      // Step 2: Now that data is prepared, submit reach calculation
      setProcessingData(false);
      setSubmitting(true);
      
      console.log("Submitting calculation request with mix:", filteredChannelMix);
      const calcResponse = await axios.post(
        'https://n8n.iniai.cloud/webhook/reach-calculation',
        {
          audienceName: formData.audienceName,
          budget: formData.budget,
          channelMix: filteredChannelMix
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY
          }
        }
      );
      
      console.log("Calculation response:", calcResponse.data);
      
      // Response should include a calculation ID for retrieving results
      if (calcResponse.data && calcResponse.data.id) {
        setCalculationId(calcResponse.data.id);
        navigate(`/reachcurve/result/${calcResponse.data.id}`);
      } else {
        throw new Error('No calculation ID returned');
      }
    } catch (error) {
      console.error('Error in workflow:', error);
      setError(error.response?.data?.message || 'Failed to process request. Please try again.');
    } finally {
      setProcessingData(false);
      setSubmitting(false);
    }
  };

  const handleEvenDistribution = () => {
    const channelCount = Object.keys(formData.channelMix).length;
    if (channelCount === 0) return;
    
    const evenValue = Math.floor(100 / channelCount);
    let remainder = 100 - (evenValue * channelCount);
    
    const newMix = {};
    Object.keys(formData.channelMix).forEach((channel, index) => {
      if (index === 0 && remainder > 0) {
        newMix[channel] = evenValue + remainder;
      } else {
        newMix[channel] = evenValue;
      }
    });
    
    setFormData(prev => ({
      ...prev,
      channelMix: newMix
    }));
  };

  const handleBackToAgent = () => {
    navigate('/agent/affinity');
  };

  if (loading && !showAudienceSelector) {
    return (
      <div className="reach-curve-loading">
        <Loader2 className="loading-icon" />
        <p>Loading channel data...</p>
      </div>
    );
  }
  
  // Show audience selector if no audience is selected
  if (showAudienceSelector) {
    return (
      <div className="reach-curve-planner">
        <div className="planner-header">
          <div className="planner-title">
            <LineChart className="planner-icon" />
            <h1>Reach Curve Planner</h1>
          </div>
          <p className="planner-description">
            Define your budget allocation across channels to generate a reach curve
          </p>
        </div>
        
        <div className="audience-selector">
          <h2>Select an Audience</h2>
          {audienceLoading ? (
            <div className="loading-indicator">Loading audiences...</div>
          ) : availableAudiences.length === 0 ? (
            <div className="no-audiences">
              <p>No audiences found. Please create an audience first.</p>
              <button onClick={() => navigate('/agent/audience')} className="create-audience-btn">
                Create Audience
              </button>
            </div>
          ) : (
            <div className="audience-list">
              {availableAudiences.map((audience) => (
                <div 
                  key={audience.id} 
                  className="audience-card"
                  onClick={() => handleAudienceSelect(audience)}
                >
                  <h3>{audience['Audience Name']}</h3>
                  <div className="audience-details">
                    <span className="audience-size">{audience['Audience Size'].toLocaleString()} people</span>
                    <span className="audience-market">{audience['Audience Market']}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="reach-curve-planner">
      <div className="planner-header">
        <div className="planner-title">
          <LineChart className="planner-icon" />
          <h1>Reach Curve Planner</h1>
        </div>
        <p className="planner-description">
          Define your budget allocation across channels to generate a reach curve
        </p>
      </div>
      
      <div className="audience-reference">
        <div className="audience-header">
          <h3>Target Audience:</h3>
          <button 
            className="change-audience-btn" 
            onClick={handleChangeAudience}
            title="Select a different audience"
          >
            Change
          </button>
        </div>
        <div className="audience-badge">
          <span className="audience-name">{currentAudience?.name}</span>
          <span className="audience-size">{currentAudience?.size}</span>
          <span className="audience-market">{currentAudience?.market}</span>
        </div>
      </div>
      
      {error && (
        <div className="error-message">
          <AlertTriangle className="error-icon" />
          <p>{error}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="planner-form">
        <div className="budget-section">
          <h2>Campaign Budget</h2>
          <div className="budget-input">
            <span className="currency-symbol">$</span>
            <input 
              type="number" 
              id="budget"
              name="budget"
              value={formData.budget}
              onChange={handleBudgetChange}
              min="1000"
              step="1000"
              required
            />
          </div>
        </div>
        
        <div className="channel-allocation-section">
          <div className="allocation-header">
            <h2>Channel Allocation</h2>
            <div className="allocation-total">
              <span className={totalAllocation === 100 ? 'valid-total' : 'invalid-total'}>
                {totalAllocation}%
              </span>
              <span className="total-label">
                {totalAllocation === 100 ? (
                  <Check className="valid-icon" />
                ) : (
                  `(need ${100 - totalAllocation}% more)`
                )}
              </span>
            </div>
          </div>
          
          <button 
            type="button" 
            className="even-distribution-button"
            onClick={handleEvenDistribution}
          >
            Distribute Evenly
          </button>
          
          <div className="channels-grid">
            {availableChannels.map(channel => (
              <div key={channel.id} className="channel-item">
                <label htmlFor={`channel-${channel.id}`}>
                  {channel.name} ({channel.platform})
                </label>
                <div className="allocation-input-wrapper">
                  <input
                    type="number"
                    id={`channel-${channel.id}`}
                    value={formData.channelMix[channel.name] || 0}
                    onChange={(e) => handleChannelAllocationChange(channel.name, e.target.value)}
                    min="0"
                    max="100"
                    className="allocation-input"
                  />
                  <span className="percent-sign">%</span>
                </div>
                <div className="budget-preview">
                  ${Math.round((formData.channelMix[channel.name] || 0) * formData.budget / 100).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="planner-actions">
          <button 
            type="button" 
            className="back-button"
            onClick={handleBackToAgent}
            disabled={submitting || processingData}
          >
            <ArrowLeft className="button-icon" />
            Back to Agent
          </button>
          <button 
            type="submit" 
            className="calculate-button"
            disabled={submitting || processingData || totalAllocation !== 100}
          >
            {processingData ? (
              <>
                <Loader2 className="button-icon spinning" />
                Preparing data...
              </>
            ) : submitting ? (
              <>
                <Loader2 className="button-icon spinning" />
                Calculating...
              </>
            ) : (
              <>
                Generate Reach Curve
                <ArrowRight className="button-icon" />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ReachCurvePlanner;